import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BellIcon, CalendarDaysIcon, MapPinIcon, SparklesIcon, Trash2Icon, TriangleAlertIcon, TruckIcon } from 'lucide-react';
import { categoryLabels, notices as seedNotices, type Notice, type NoticeCategory } from '../../data/avis';
const icons: Record<NoticeCategory, typeof BellIcon> = {
  urgent: TriangleAlertIcon,
  service: TruckIcon,
  loisirs: CalendarDaysIcon,
  personnalise: SparklesIcon,
  municipal: BellIcon
};
const filters = ['Tous', 'Importants', 'Non lus', 'Services'] as const;
export type NoticeFilter = (typeof filters)[number];
interface NoticeFeedProps {
  filter: NoticeFilter;
  onFilterChange: (filter: NoticeFilter) => void;
}
export function NoticeFeed({
  filter,
  onFilterChange
}: NoticeFeedProps) {
  const [items, setItems] = useState<Notice[]>(seedNotices);
  const [removed, setRemoved] = useState<Notice | null>(null);
  const visible = useMemo(() => {
    if (filter === 'Importants') return items.filter(item => item.category === 'urgent');
    if (filter === 'Non lus') return items.filter(item => item.unread);
    if (filter === 'Services') return items.filter(item => item.category === 'service');
    return items;
  }, [filter, items]);
  const toggleRead = (id: string) => setItems(current => current.map(item => item.id === id ? {
    ...item,
    unread: !item.unread
  } : item));
  const remove = (notice: Notice) => {
    setItems(current => current.filter(item => item.id !== notice.id));
    setRemoved(notice);
    window.setTimeout(() => setRemoved(value => value?.id === notice.id ? null : value), 5000);
  };
  const undo = () => {
    if (!removed) return;
    setItems(current => [removed, ...current]);
    setRemoved(null);
  };
  return <section aria-labelledby="feed-titre" className="mt-14" data-id="element-415" data-mp="8e4obqm6eqvi">
      <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-416" data-mp="8gqt5ihomsa1">
        <h2 id="feed-titre" className="text-2xl font-extrabold tracking-tight text-ville-navy" data-id="element-417" data-mp="dfahfpjr0g5k">
          Vos notifications
        </h2>
        <ul className="flex flex-wrap gap-2" data-id="element-418" data-mp="2b1bcul9hbukv">
          {filters.map(option => <li key={option} data-id="element-419" data-mp="2b3nsgrrrlsur">
              <button type="button" aria-pressed={filter === option} onClick={() => onFilterChange(option)} className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${filter === option ? 'bg-ville-indigo text-white' : 'border border-slate-200 bg-white text-ville-navy hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-420" data-mp="26a1orp3kwzb3">
                {option}
              </button>
            </li>)}
        </ul>
      </div>

      {visible.length === 0 ? <p className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500" data-id="element-421" data-mp="3crovs4epef4n">
          Aucun avis dans cette vue.
        </p> : <ul className="mt-5 space-y-3" data-id="element-422" data-mp="3p87r33be47cs">
          {visible.map(notice => {
        const Icon = icons[notice.category];
        const urgent = notice.category === 'urgent';
        return <li key={notice.id} className={`group relative rounded-2xl border bg-white px-5 py-4 transition-colors duration-150 ease-ville ${urgent ? 'border-slate-200 border-l-4 border-l-red-600' : 'border-slate-200'}`} data-id="element-423" data-mp="2sd9p90snog9">
                <div className="flex items-start gap-4" data-id="element-424" data-mp="3b7wynvufmkpi">
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${urgent ? 'bg-red-50 text-red-600' : 'bg-ville-indigo-soft text-ville-indigo'}`} aria-hidden="true" data-id="element-425" data-mp="3g945nsor3fv4">
                    <Icon className="h-5 w-5" data-id="element-426" data-mp="t3fet4fr7mgq" />
                  </span>

                  <div className="min-w-0 flex-1" data-id="element-427" data-mp="t5ib267i2btw">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-428" data-mp="p355piwo0fxn">
                      {notice.unread && <span className="h-2 w-2 rounded-full bg-ville-indigo" aria-label="Non lu" data-id="element-429" data-mp="133fzatpd08mi" />}
                      <h3 className={`text-[15px] leading-snug ${notice.unread ? 'font-extrabold text-ville-navy' : 'font-semibold text-slate-600'}`} data-id="element-430" data-mp="y4uuegz5x0rq">
                        {notice.title}
                      </h3>
                      <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${urgent ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600'}`} data-id="element-431" data-mp="1gt9m76hb0itl">
                        {categoryLabels[notice.category]}
                      </span>
                    </div>
                    <p className={`mt-1 text-sm leading-relaxed ${notice.unread ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-432" data-mp="2eeudye3ut0b6">
                      {notice.description}
                    </p>
                    <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500" data-id="element-433" data-mp="20hfk8x6ki6de">
                      <span data-id="element-434" data-mp="1qp9ttrvyrx0b">{notice.when}</span>
                      {notice.context && <span className="flex items-center gap-1" data-id="element-435" data-mp="1qnf6k6x7zst2">
                          <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-436" data-mp="1vhjf2um4w8w9" />
                          {notice.context}
                        </span>}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-1" data-id="element-437" data-mp="1mlag1h22jcan">
                    <button type="button" onClick={() => toggleRead(notice.id)} className="rounded-full px-3 py-1.5 text-xs font-bold text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-438" data-mp="17qxibzhkyb1k">
                      {notice.unread ? 'Marquer comme lu' : 'Non lu'}
                    </button>
                    <button type="button" onClick={() => remove(notice)} aria-label={`Supprimer l’avis ${notice.title}`} className="grid h-9 w-9 place-items-center rounded-full text-slate-400 opacity-0 transition-[opacity,color] duration-150 ease-ville hover:text-ville-navy focus-visible:opacity-100 group-hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-439" data-mp="exixfd797bo7">
                      <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-440" data-mp="3nd2ms14t09xz" />
                    </button>
                  </div>
                </div>
              </li>;
      })}
        </ul>}

      <AnimatePresence data-id="element-441" data-mp="1xsknd6rg4ho5">
        {removed && <motion.div role="status" initial={{
        y: 12,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 8,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-4 rounded-full bg-ville-navy px-5 py-3 text-sm text-white shadow-xl" data-id="element-442" data-mp="22syxcucly1ot">
            Notification supprimée
            <button type="button" onClick={undo} className="font-bold text-ville-lime underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-443" data-mp="2lm2regtqmgjy">
              Annuler
            </button>
          </motion.div>}
      </AnimatePresence>
    </section>;
}